import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminPage } from "./components/AdminPage";
import {
  BlockIntro,
  BreakScreen,
  CompleteScreen,
  ComprehensionScreen,
  ConditionEvaluation,
  FinalEvaluation,
  PracticeScreen,
  PreQuestionnaire,
} from "./components/FormScreens";
import { ReadingScreen } from "./components/ReadingScreen";
import { SiteHeader } from "./components/SiteHeader";
import { storyForBlock } from "./data/story";
import { createInitialState, makeId } from "./lib/experiment";
import {
  appendEventLocally,
  loadState,
  persistRemoteEvent,
  persistRemoteState,
  resetLocalPrototype,
  saveStateLocally,
} from "./lib/storage";
import type { ExperimentEvent, ExperimentState, Stage } from "./types";

type SaveStatus = "saved" | "saving" | "local" | "error";

function App() {
  if (window.location.pathname.startsWith("/admin")) return <AdminPage />;
  return <ParticipantApp />;
}

function ParticipantApp() {
  const [state, setState] = useState<ExperimentState>(() => loadState() ?? createInitialState(true));
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("local");
  const saveTimer = useRef<number | undefined>(undefined);
  const positionRef = useRef({ current: state.currentViewportIndex, furthest: state.furthestReadIndex });
  const story = useMemo(() => storyForBlock(state.blockNumber), [state.blockNumber]);
  const condition = state.sequence[Math.min(state.blockNumber, 2)];

  useEffect(() => {
    positionRef.current = {
      current: state.currentViewportIndex,
      furthest: state.furthestReadIndex,
    };
  }, [state.blockNumber]);

  useEffect(() => {
    saveStateLocally(state);
    setSaveStatus("saving");
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        await persistRemoteState(state);
        setSaveStatus("saved");
      } catch {
        setSaveStatus("local");
      }
    }, 900);
    return () => window.clearTimeout(saveTimer.current);
  }, [state]);

  const logEvent = useCallback((eventType: string, payload?: Record<string, unknown>) => {
    const event: ExperimentEvent = {
      eventId: makeId("event"),
      sessionId: state.sessionId,
      participantCode: state.participantCode,
      eventType,
      occurredAt: new Date().toISOString(),
      stage: state.stage,
      blockNumber: state.blockNumber,
      payload,
    };
    appendEventLocally(event);
    void persistRemoteEvent(event).catch(() => setSaveStatus("local"));
  }, [state.blockNumber, state.participantCode, state.sessionId, state.stage]);

  const transition = (stage: Stage, patch: Partial<ExperimentState> = {}) => {
    logEvent("stage_completed", { from: state.stage, to: stage });
    setState((current) => ({ ...current, ...patch, stage }));
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const updatePosition = (current: number, furthest: number) => {
    const confirmedFurthest = Math.max(positionRef.current.furthest, furthest);
    if (positionRef.current.current === current && positionRef.current.furthest === confirmedFurthest) return;
    positionRef.current = { current, furthest: confirmedFurthest };
    setState((value) => ({
      ...value,
      currentViewportIndex: current,
      furthestReadIndex: Math.max(value.furthestReadIndex, confirmedFurthest),
    }));
    logEvent("reading_position_confirmed", { currentViewportIndex: current, furthestReadIndex: confirmedFurthest });
  };

  const stopExperiment = () => {
    if (!window.confirm("実験を中止しますか。理由を入力する必要はありません。")) return;
    logEvent("experiment_stopped");
    const stoppedAt = new Date().toISOString();
    transition("complete", { completedAt: stoppedAt, stoppedAt });
  };

  const reset = () => {
    resetLocalPrototype();
    setState(createInitialState(true));
    window.location.assign("/");
  };

  let content: React.ReactNode;
  switch (state.stage) {
    case "pre-questionnaire":
      content = (
        <PreQuestionnaire
          initial={state.preAnswers}
          guideVisible={state.guideVisible}
          onGuideChange={(guideVisible) => setState((current) => ({ ...current, guideVisible }))}
          onSubmit={(preAnswers) => transition("practice", { preAnswers })}
        />
      );
      break;
    case "practice":
      content = <PracticeScreen onContinue={() => transition("block-intro")} />;
      break;
    case "block-intro":
      content = <BlockIntro state={state} condition={condition} onContinue={() => transition("reading-first", { trialStartedAt: new Date().toISOString(), furthestReadIndex: 0, currentViewportIndex: 0, activeTab: "text" })} />;
      break;
    case "reading-first":
      content = <ReadingScreen state={state} story={story} condition={condition} phase="first" onPosition={updatePosition} onTab={(activeTab) => { setState((current) => ({ ...current, activeTab })); logEvent("reader_tab_changed", { activeTab }); }} onEvent={logEvent} onContinue={() => transition("break", { breakStartedAt: new Date().toISOString(), activeTab: "text" })} />;
      break;
    case "break":
      content = <BreakScreen startedAt={state.breakStartedAt ?? new Date().toISOString()} onComplete={() => transition("reading-second", { activeTab: "text", breakStartedAt: undefined })} />;
      break;
    case "reading-second":
      content = <ReadingScreen state={state} story={story} condition={condition} phase="second" onPosition={updatePosition} onTab={(activeTab) => { setState((current) => ({ ...current, activeTab })); logEvent("reader_tab_changed", { activeTab }); }} onEvent={logEvent} onContinue={() => transition("comprehension", { activeTab: "text" })} />;
      break;
    case "comprehension":
      content = <ComprehensionScreen story={story} onSubmit={(comprehension) => { sessionStorage.setItem("current-comprehension", JSON.stringify(comprehension)); transition("condition-evaluation"); }} />;
      break;
    case "condition-evaluation":
      content = <ConditionEvaluation onSubmit={(evaluation) => {
        const comprehension = JSON.parse(sessionStorage.getItem("current-comprehension") ?? "{}") as Record<string, string>;
        const nextTrials = [...state.trials, { blockNumber: state.blockNumber, condition, storyId: story.id, comprehension, evaluation, furthestReadIndex: state.furthestReadIndex, startedAt: state.trialStartedAt ?? new Date().toISOString(), completedAt: new Date().toISOString() }];
        sessionStorage.removeItem("current-comprehension");
        if (state.blockNumber < 2) transition("block-intro", { trials: nextTrials, blockNumber: state.blockNumber + 1, furthestReadIndex: 0, currentViewportIndex: 0, trialStartedAt: undefined });
        else transition("final-evaluation", { trials: nextTrials });
      }} />;
      break;
    case "final-evaluation":
      content = <FinalEvaluation onSubmit={(finalAnswers) => transition("complete", { finalAnswers, completedAt: new Date().toISOString() })} />;
      break;
    case "complete":
      content = <CompleteScreen state={state} onReset={reset} />;
      break;
  }

  return (
    <div className="app-frame">
      <div className="test-banner">TEST ENVIRONMENT　このサイトは本実験用の機能検証版です</div>
      <SiteHeader state={state} saveStatus={saveStatus} onStop={stopExperiment} />
      {content}
      <footer className="site-footer"><span>MAIN EXPERIMENT PROTOTYPE · v0.1.0</span><a href="/admin">管理画面</a></footer>
    </div>
  );
}

export default App;
